import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Paper,
  Tooltip,
  Stack,
  Switch,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LinkIcon from "@mui/icons-material/Link";
import CachedIcon from "@mui/icons-material/Cached";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { AI_MODEL_PROVIDERS } from "../../enum/enums";
import { useAiModelProviders } from "../../hooks/useAiModelProviders";
import { StorageHelper } from "../../utils/storageHelper";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import { ENV } from "../../utils/configs";

const createDefaultConnections = () => [
  {
    id: 1,
    baseUrl: ENV.openai_base_url,
    apiKey: "",
    provider: "solutionec",
    enabled: true,
    showApiKey: false,
  },
];

export const Connection = ({ onClose, onSaved, onModelsRefresh, disableCancel = false }) => {
  const [connections, setConnections] = useState(createDefaultConnections);
  const [verificationState, setVerificationState] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const {
    addAiModelProviders,
    listAiModelProviders,
    deleteAiModelProvider,
    updateAiModelProviderStatus,
    loading: savingProviders,
  } =
    useAiModelProviders();

  useEffect(() => {
    let isMounted = true;

    const loadProviders = async () => {
      setIsLoadingProviders(true);
      const userId = StorageHelper.get("userDetails")?.user_id;
      if (!userId) {
        setIsLoadingProviders(false);
        return;
      }

      const result = await listAiModelProviders(userId);
      if (!isMounted) return;

      if (result.success && Array.isArray(result.data) && result.data.length) {
        setConnections(
          result.data.map((row, index) => ({
            id: row.ai_model_provider_id ?? index + 1,
            ai_model_provider_id: row.ai_model_provider_id,
            baseUrl: row.base_url || "",
            // api_key is already decrypted on the backend
            apiKey: row.api_key || "",
            provider: row.provider_name || "openai",
            enabled: row.is_enabled !== false,
            showApiKey: false,
          }))
        );
      } else if (!result.success && result.error) {
        setSnackbar({
          open: true,
          message: result.error,
          severity: "error",
        });
      }
      setIsLoadingProviders(false);
    };

    loadProviders();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleChange = (id, field, value) => {
    setConnections((prev) =>
      prev.map((conn) =>
        conn.id === id
          ? {
              ...conn,
              [field]: value,
            }
          : conn
      )
    );
  };

  const handleAdd = () => {
    setConnections((prev) => {
      const nextId = prev.reduce((max, c) => Math.max(max, c.id), 0) + 1;
      return [
        ...prev,
        {
          id: nextId,
          baseUrl: "",
          apiKey: "",
          // New rows default to OpenAI provider
          provider: "openai",
          enabled: true,
          showApiKey: false,
        },
      ];
    });
  };

  const handleRemove = (id) => {
    const target = connections.find((conn) => conn.id === id);
    if (!target) return;

    // Prevent removing the last visible connection to avoid empty state,
    // same UX as before.
    if (connections.length === 1) return;

    setDeleteTarget(target);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);

    try {
      if (deleteTarget.ai_model_provider_id) {
        const userId = StorageHelper.get("userDetails")?.user_id;
        if (!userId) {
          setSnackbar({
            open: true,
            message: "Unable to delete: user is not available.",
            severity: "error",
          });
          return;
        }

        const result = await deleteAiModelProvider(
          deleteTarget.ai_model_provider_id,
          userId
        );

        if (!result.success) {
          setSnackbar({
            open: true,
            message: result.error || "Failed to delete AI model provider.",
            severity: "error",
          });
          return;
        }
      }

      setConnections((prev) => prev.filter((conn) => conn.id !== deleteTarget.id));
      setSnackbar({
        open: true,
        message: "Connection deleted.",
        severity: "success",
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    }
  };

  const handleToggleEnabled = (id, checked) => {
    setConnections((prev) => {
      const next = prev.map((conn) =>
        conn.id === id
          ? {
              ...conn,
              enabled: checked,
            }
          : conn
      );

      const target = next.find((conn) => conn.id === id);

      // For persisted connections, send is_enabled update to backend.
      if (target?.ai_model_provider_id) {
        const userId = StorageHelper.get("userDetails")?.user_id;
        if (!userId) {
          setSnackbar({
            open: true,
            message: "Unable to update: user is not available.",
            severity: "error",
          });
          return prev; // revert local change
        }

        // Fire and forget; on error, revert and show snackbar.
        (async () => {
          const result = await updateAiModelProviderStatus(
            target.ai_model_provider_id,
            userId,
            checked
          );

          if (!result.success) {
            setConnections((innerPrev) =>
              innerPrev.map((conn) =>
                conn.id === id
                  ? {
                      ...conn,
                      enabled: !checked,
                    }
                  : conn
              )
            );
            setSnackbar({
              open: true,
              message: result.error || "Failed to update connection.",
              severity: "error",
            });
          } else {
            // Refresh model list so the chat dropdown reflects enabled/disabled providers
            onModelsRefresh?.();
          }
        })();
      }

      return next;
    });
  };

  const handleVerify = async (connection) => {
    const { id, baseUrl, apiKey } = connection;
    if (!baseUrl || !apiKey) {
      setVerificationState((prev) => ({
        ...prev,
        [id]: { status: "error", message: "Add URL and API key first." },
      }));
      return;
    }

    setVerificationState((prev) => ({
      ...prev,
      [id]: { status: "verifying", message: "Verifying connection…" },
    }));

    try {
      const url = `${baseUrl.replace(/\/+$/, "")}/models`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${apiKey}`,
        },
      });

      if (response.ok) {
        const providerMeta =
          AI_MODEL_PROVIDERS.find((p) => p.value === connection.provider) ||
          null;
        const providerLabel = providerMeta?.label || "OpenAI";

        setVerificationState((prev) => ({
          ...prev,
          [id]: { status: "success", message: "Connection verified." },
        }));
        setSnackbar({
          open: true,
          message: `${providerLabel} connection verified successfully.`,
          severity: "success",
        });
      } else {
        setVerificationState((prev) => ({
          ...prev,
          [id]: {
            status: "error",
            message: `Verification failed (${response.status}).`,
          },
        }));
      }
    } catch (err) {
      setVerificationState((prev) => ({
        ...prev,
        [id]: {
          status: "error",
          message: "Unable to reach API. Check CORS/network.",
        },
      }));
    }
  };

  const handleCloseSnackbar = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSave = async () => {
    // Prepare payload for GraphQL mutation
    const providersPayload = connections
      .map((conn) => {
        const providerMeta =
          AI_MODEL_PROVIDERS.find((p) => p.value === conn.provider) || null;
        const provider_name =
          providerMeta?.value || conn.provider;

        return {
          provider_name,
          base_url: conn.baseUrl || "",
          api_key: conn.apiKey || "",
          is_enabled: conn.enabled !== false,
          is_active: true,
        };
      })
      // Require base_url and api_key to avoid sending empty rows
      .filter((p) => p.base_url && p.api_key);

    if (!providersPayload.length) {
      setSnackbar({
        open: true,
        message: "Add at least one provider with URL and API key.",
        severity: "error",
      });
      return;
    }

    const userId = StorageHelper.get("userDetails")?.user_id;
    if (!userId) {
      setSnackbar({
        open: true,
        message: "Unable to save: user is not available.",
        severity: "error",
      });
      return;
    }

    const result = await addAiModelProviders(providersPayload, userId);

    if (!result.success) {
      setSnackbar({
        open: true,
        message: result.error || "Failed to save AI model providers.",
        severity: "error",
      });
      return;
    }

    setSnackbar({
      open: true,
      message: "Connection settings saved successfully.",
      severity: "success",
    });

    if (onSaved) {
      await onSaved();
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        pb: 2.5,
        bgcolor: "#f8fafc",
        maxHeight: "85vh",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2.5,
        }}
      >
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: "#0f172a", mb: 0.5 }}
          >
            AI Model Providers
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#64748b", fontSize: "0.8125rem" }}
          >
            Manage AI model provider connections used by Ask Flashdice.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Add connection">
            <IconButton
              size="small"
              onClick={handleAdd}
              sx={{
                borderRadius: 2,
                border: "1px solid #e2e8f0",
                bgcolor: "#ffffff",
                color: "#3f49a7",
                "&:hover": {
                  bgcolor: "#e5edff",
                },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          pr: 0.5,
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-track": {
            background: "#f1f5f9",
            borderRadius: 4,
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#cbd5e1",
            borderRadius: 4,
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#94a3b8",
          },
        }}
      >
      {isLoadingProviders ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 6,
          }}
        >
          <CircularProgress size={28} sx={{ color: "rgb(44, 52, 123)" }} />
        </Box>
      ) : (
      <Stack spacing={1.5}>
        {connections.map((connection) => (
          <Paper
            key={connection.id}
            elevation={0}
            sx={{
              px: 1.75,
              py: 1.25,
              borderRadius: 1.5,
              border: "1px solid #e2e8f0",
              bgcolor: "#ffffff",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.25,
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  bgcolor: "#eef2ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  mt: 0.5,
                }}
              >
                <LinkIcon sx={{ fontSize: 18, color: "#3f49a7" }} />
              </Box>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Provider"
                  select
                  value={connection.provider || "openai"}
                  onChange={(e) =>
                    handleChange(connection.id, "provider", e.target.value)
                  }
                  disabled={savingProviders || !!connection.ai_model_provider_id}
                  InputLabelProps={{
                    sx: { fontSize: "0.8rem" },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: "0.85rem",
                      "& fieldset": {
                        borderColor: "#e2e8f0",
                        borderWidth: 1,
                      },
                      "&:hover fieldset": {
                        borderColor: "#cbd5e1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#3F49A7",
                        borderWidth: 1,
                      },
                    },
                  }}
                >
                  {AI_MODEL_PROVIDERS.map((provider) => (
                    <MenuItem key={provider.value} value={provider.value}>
                      {provider.label}
                    </MenuItem>
                  ))}
                </TextField>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    label="Base URL"
                    value={connection.baseUrl}
                    onChange={(e) =>
                      handleChange(connection.id, "baseUrl", e.target.value)
                    }
                    placeholder="https://api.openai.com/v1"
                    InputLabelProps={{
                      sx: { fontSize: "0.8rem" },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        fontSize: "0.85rem",
                        "& fieldset": {
                          borderColor: "#e2e8f0",
                          borderWidth: 1,
                        },
                        "&:hover fieldset": {
                          borderColor: "#cbd5e1",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#3F49A7",
                          borderWidth: 1,
                        },
                      },
                    }}
                  />
                  <Tooltip title="Verify connection">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleVerify(connection)}
                        sx={{
                          color: "#64748b",
                          "&:hover": { color: "#3f49a7" },
                        }}
                      >
                        {verificationState[connection.id]?.status ===
                        "verifying" ? (
                          <CircularProgress size={16} />
                        ) : (
                          <CachedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Enable/Disable">
                    <Switch
                      size="small"
                      checked={connection.enabled !== false}
                      onChange={(e) =>
                        handleToggleEnabled(connection.id, e.target.checked)
                      }
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#10b981",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#a7f3d0",
                          },
                      }}
                    />
                  </Tooltip>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  label="API key"
                  type={connection.showApiKey ? "text" : "password"}
                  value={connection.apiKey || ""}
                  onChange={(e) =>
                    handleChange(connection.id, "apiKey", e.target.value)
                  }
                  placeholder="sk-..."
                  InputLabelProps={{
                    sx: { fontSize: "0.8rem" },
                  }}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleChange(
                            connection.id,
                            "showApiKey",
                            !connection.showApiKey
                          )
                        }
                        edge="end"
                      >
                        {connection.showApiKey ? (
                          <VisibilityOffIcon fontSize="small" />
                        ) : (
                          <VisibilityIcon fontSize="small" />
                        )}
                      </IconButton>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: "0.85rem",
                      "& fieldset": {
                        borderColor: "#e2e8f0",
                        borderWidth: 1,
                      },
                      "&:hover fieldset": {
                        borderColor: "#cbd5e1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#3F49A7",
                        borderWidth: 1,
                      },
                    },
                  }}
                />
                {verificationState[connection.id]?.message && (
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 0.25,
                      color:
                        verificationState[connection.id]?.status === "success"
                          ? "#16a34a"
                          : "#dc2626",
                    }}
                  >
                    {verificationState[connection.id].message}
                  </Typography>
                )}
              </Box>
              <Tooltip title="Remove connection">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleRemove(connection.id)}
                    disabled={connections.length === 1}
                    sx={{
                      mt: 0.5,
                      color:
                        connections.length === 1 ? "#cbd5e1" : "#94a3b8",
                      "&:hover": {
                        color:
                          connections.length === 1 ? "#cbd5e1" : "#ef4444",
                      },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Paper>
        ))}
      </Stack>
      )}
      </Box>

      <ConfirmationModal
        open={deleteDialogOpen}
        title="Delete connection"
        message={`Are you sure you want to delete the selected connection? This action cannot be undone.`}
        onClose={() => {
          if (isDeleting) return;
          setDeleteDialogOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleConfirmDelete}
        buttonText={isDeleting ? "Deleting..." : "Delete"}
        colorCode="#d32f2f"
      />

      <Box
        sx={{
          flexShrink: 0,
          mt: 2,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
        }}
      >
        {onClose && (
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            sx={{
              textTransform: "none",
              fontSize: "0.8125rem",
              borderRadius: 1,
              fontWeight: 500,
              px: 2.5,
              borderColor: "#d1d5db",
              color: "#64748b",
              "&:hover": {
                borderColor: "#3F49A7",
                color: "#3F49A7",
                backgroundColor: "rgba(63,73,167,0.06)",
              },
            }}
            disabled={savingProviders || disableCancel}
          >
            Cancel
          </Button>
        )}
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          sx={{
            textTransform: "none",
            borderRadius: 1,
            fontWeight: 500,
            fontSize: "0.8125rem",
            px: 2.5,
            backgroundColor: "#3F49A7",
            "&:hover": { backgroundColor: "#323a8a" },
          }}
          disabled={savingProviders}
        >
          {savingProviders ? "Saving..." : "Save"}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ fontSize: "0.8rem" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Connection;

